<template>
  <div class="home">
    <!-- Hero -->
    <section class="hero">
      <img :src="bgHero" alt="" class="hero-bg-art" aria-hidden="true" />
      <ParticleField variant="dark" :count="52" />
      <div class="container hero-layout">
        <div class="hero-text">
          <p class="overline reveal">芊荨科技 · 官方产品中心</p>
          <h1 class="reveal">Windows 效率工具<br />专业、稳定、本地运行</h1>
          <p class="hero-desc reveal">
            十款独立桌面软件，面向真实办公与创作场景。
            无广告、无插件、数据不上传，支持 Windows 7 / 8 / 10 / 11。
          </p>
          <div class="hero-actions reveal">
            <a href="#products" class="btn btn-white">查看全部产品</a>
            <a href="#about" class="btn btn-outline-light">关于我们</a>
          </div>
        </div>
        <div class="hero-gallery reveal" style="transition-delay:80ms">
          <img
            v-for="(shot, i) in galleryShots"
            :key="shot.alt"
            :src="shot.src"
            :alt="shot.alt"
            class="app-shot gallery-shot"
            :class="`gallery-shot--${i}`"
            loading="lazy"
          />
          <img
            :src="gifmakerHeroShot"
            alt="芊荨视频转GIF制作"
            class="app-shot gallery-shot gallery-shot--overlay"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <!-- Products -->
    <section class="showcase" id="products">
      <img :src="bgSection" alt="" class="showcase-bg-art" aria-hidden="true" />
      <img :src="bgShowcaseOrbs" alt="" class="showcase-orbs-art" aria-hidden="true" />
      <ParticleField variant="light" :count="28" />
      <div class="container">
        <header class="showcase-header reveal">
          <p class="overline">Products</p>
          <h2>产品矩阵</h2>
          <p>每一款独立安装、免费下载，专注解决一个核心问题。</p>
        </header>
      </div>

      <article
        v-for="(p, i) in products"
        :key="p.title"
        class="showcase-item reveal"
        :class="{ 'showcase-item--alt': i % 2 === 1 }"
      >
        <div class="container showcase-row">
          <div class="showcase-copy">
            <span class="showcase-index">{{ String(i + 1).padStart(2, '0') }}</span>
            <div class="showcase-title-row">
              <img :src="p.icon" :alt="p.title" class="showcase-icon" />
              <div>
                <h3>{{ p.title }}</h3>
                <p class="showcase-tag">{{ p.tagline }}</p>
              </div>
            </div>
            <p class="showcase-desc">{{ p.description }}</p>
            <ul class="showcase-features">
              <li v-for="f in p.features" :key="f">{{ f }}</li>
            </ul>
            <div class="showcase-actions">
              <a :href="p.downloadLink" :download="p.downloadFileName" class="btn-download">
                <img src="@/assets/videocvt/Windows.png" alt="" class="btn-os-icon" />
                免费下载
              </a>
              <router-link :to="p.route" class="link-arrow">产品详情</router-link>
            </div>
          </div>
          <div class="showcase-preview shot-glow">
            <img :src="p.preview" :alt="`${p.title}界面预览`" class="app-shot" loading="lazy" />
          </div>
        </div>
      </article>
    </section>

    <!-- Standards -->
    <section class="standards">
      <img :src="bgStandards" alt="" class="standards-bg-art" aria-hidden="true" />
      <ParticleField variant="dark" :count="36" />
      <div class="container">
        <header class="standards-header reveal">
          <p class="overline">Standards</p>
          <h2>我们的基本标准</h2>
        </header>
        <div class="standards-grid reveal">
          <div v-for="s in standards" :key="s.title" class="standard">
            <span class="standard-num">{{ s.num }}</span>
            <h4>{{ s.title }}</h4>
            <p>{{ s.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- About -->
    <section class="about" id="about">
      <img :src="bgAbout" alt="" class="about-bg-art" aria-hidden="true" />
      <SectionBackdrop :particles="true" variant="light" :count="18" />
      <div class="container about-layout reveal">
        <div class="about-copy">
          <p class="overline">About</p>
          <h2>鄂州市芊荨科技有限公司</h2>
          <p>
            我们专注于 Windows 平台的桌面工具开发。不做功能堆砌，
            以长期维护、稳定迭代为原则，为用户提供可信赖的效率软件。
          </p>
          <p class="about-contact">
            联系邮箱：<a href="mailto:mtysoft@qq.com">mtysoft@qq.com</a>
          </p>
        </div>
        <div class="about-stats">
          <div v-for="m in metrics" :key="m.label" class="about-stat">
            <strong>{{ m.value }}</strong>
            <span>{{ m.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta">
      <img :src="bgCta" alt="" class="cta-bg-art" aria-hidden="true" />
      <ParticleField variant="light" :count="22" />
      <div class="container cta-inner reveal">
        <h2>选择适合你的工具，立即开始使用</h2>
        <a href="#products" class="btn btn-dark">浏览产品并下载</a>
      </div>
    </section>
  </div>
</template>

<script>
import ParticleField from '@/components/ParticleField.vue'
import SectionBackdrop from '@/components/SectionBackdrop.vue'

export default {
  name: 'Home',
  components: { ParticleField, SectionBackdrop },
  data() {
    return {
      observer: null,
      bgHero: require('@/assets/illustrations/bg-hero-accent.svg'),
      bgSection: require('@/assets/illustrations/bg-section-flow.svg'),
      bgShowcaseOrbs: require('@/assets/illustrations/bg-showcase-orbs.svg'),
      bgStandards: require('@/assets/illustrations/bg-standards-glow.svg'),
      bgAbout: require('@/assets/illustrations/bg-about-rings.svg'),
      bgCta: require('@/assets/illustrations/bg-cta-soft.svg'),
      galleryShots: [
        { src: require('@/assets/fatestar/首页.png'), alt: '周易星运八字测算大师' },
        { src: require('@/assets/longshot/首页.png'), alt: '芊荨滚动长截图' },
        { src: require('@/assets/Everyfile/详细视图.png'), alt: 'Everyfile' },
        { src: require('@/assets/searchfile/文件名搜索-详细视图.png'), alt: '芊荨搜索文件文本' },
        { src: require('@/assets/luckynote/全部笔记.png'), alt: '芊荨记事本笔记' },
        { src: require('@/assets/earthview/地球-天安门.png'), alt: '谷哥地球-天地图' }
      ],
      gifmakerHeroShot: require('@/assets/gifmaker/首页.png'),
      metrics: [
        { value: '10', label: '核心产品' },
        { value: '100%', label: '本地运行' },
        { value: '0', label: '广告插件' },
        { value: '免费', label: '永久使用' }
      ],
      standards: [
        { num: '01', title: '本地优先', text: '核心功能在本地完成，不上传用户数据，不依赖云端服务。' },
        { num: '02', title: '稳定可靠', text: '面向长期日常使用设计，注重兼容性与版本稳定性。' },
        { num: '03', title: '界面克制', text: '清晰的信息层级，减少干扰，让用户快速完成目标任务。' },
        { num: '04', title: '持续维护', text: '基于用户反馈持续迭代，保持与 Windows 新版本的兼容。' }
      ],
      products: [
        {
          title: '芊荨视频转GIF制作',
          shortName: '视频转 GIF',
          tagline: '视频转 GIF · 屏幕录制 · 编辑台',
          icon: require('@/assets/gifmaker/logo_64x64.png'),
          preview: require('@/assets/gifmaker/首页.png'),
          description: '本地转换不上传，视频导入与屏幕录制共用编辑台，裁时间轴、控体积，搭配 GIF 工具箱完成动图制作。',
          features: ['视频 / 录屏转 GIF，三档品质预设', '编辑台裁切与批量转换', '压缩、倒放、多图合成与格式转换'],
          route: '/product/gifmaker',
          downloadLink: '/ftp/packages/gifmaker/GifMakerSetup.exe',
          downloadFileName: 'GifMakerSetup.exe'
        },
        {
          title: '芊荨桌面时钟壁纸',
          shortName: '桌面时钟',
          tagline: '翻页时钟 · 桌面挂件 · 全屏屏保',
          icon: require('@/assets/deskclock/deskclock_64x64.png'),
          preview: require('@/assets/deskclock/时钟中心.png'),
          description: '透明翻页与插画桌面时钟，支持鼠标穿透、农历显示与全屏屏保，让桌面时间展示既专业又实用。',
          features: ['多种时钟主题与翻页动效', '桌面挂件与鼠标穿透', '全屏屏保与农历显示'],
          route: '/product/deskclock',
          downloadLink: '/ftp/packages/deskclock/DeskClockSetup.exe',
          downloadFileName: 'DeskClockSetup.exe'
        },
        {
          title: '芊荨音视频转换器',
          shortName: '音视频转换',
          tagline: '格式互转 · 批量处理 · 高清输出',
          icon: require('@/assets/videocvt/videoconvert_64x64.png'),
          preview: require('@/assets/videocvt/mainpage.png'),
          description: '支持主流视频和音频格式互转，提供合并、分割、水印、压缩等完整媒体处理能力。',
          features: ['视频 / 音频多格式互转', '批量处理与参数可调', '合并、分割、水印、转 GIF'],
          route: '/product/video-converter',
          downloadLink: '/ftp/packages/videocvt/VideoCvtSetup.exe',
          downloadFileName: 'VideoCvtSetup.exe'
        },
        {
          title: '芊荨压缩',
          shortName: '文件压缩',
          tagline: 'ZIP / 7Z / RAR · 加密打包',
          icon: require('@/assets/mtyzip/zip_logo64.png'),
          preview: require('@/assets/mtyzip/首页.png'),
          description: '高效稳定的压缩解压工具，支持多格式、加密打包、压缩预览与专项压缩。',
          features: ['ZIP / 7Z / RAR 多格式', '压缩预览与密码保护', 'PDF / 图片 / 视频专项压缩'],
          route: '/product/compress',
          downloadLink: '/ftp/packages/mtyzip/MtyZipSetup.exe',
          downloadFileName: 'MtyZipSetup.exe'
        },
        {
          title: 'Everyfile',
          shortName: 'Everyfile',
          tagline: '毫秒级 · 全盘文件检索',
          icon: require('@/assets/Everyfile/everyfile_64x64.png'),
          preview: require('@/assets/Everyfile/详细视图.png'),
          description: '高性能文件索引引擎，支持关键字搜索、多维过滤、多视图浏览与收藏夹管理。',
          features: ['全盘秒级索引检索', '列表 / 图标多视图', '收藏夹与智能过滤'],
          route: '/product/everyfile',
          downloadLink: '/ftp/packages/Everyfile/EveryfileSetup.exe',
          downloadFileName: 'EveryfileSetup.exe'
        },
        {
          title: '芊荨搜索文件文本',
          shortName: '搜索文件',
          tagline: '文件名搜索 · 内容检索替换',
          icon: require('@/assets/searchfile/searchfile_64x64.png'),
          preview: require('@/assets/searchfile/文件名搜索-详细视图.png'),
          description: '文件名秒搜与文件内容检索一体，支持多视图浏览、收藏夹管理与文本批量替换。',
          features: ['全盘文件名极速检索', '内容搜索与批量替换', '列表 / 图标多视图'],
          route: '/product/searchfile',
          downloadLink: '/ftp/packages/searchfile/SearchfileSetup.exe',
          downloadFileName: 'SearchfileSetup.exe'
        },
        {
          title: '芊荨记事本笔记',
          shortName: '记事本笔记',
          tagline: '本地笔记 · 日记待办 · 标签',
          icon: require('@/assets/luckynote/luckynote_64x64.png'),
          preview: require('@/assets/luckynote/全部笔记.png'),
          description: '三栏本地笔记工具，支持笔记本分类、日记待办、标签整理与全文搜索，数据存本机不上传。',
          features: ['笔记本分类与富文本', '日记 / 待办清单', '标签、搜索与回收站'],
          route: '/product/luckynote',
          downloadLink: '/ftp/packages/luckynote/LuckyNoteSetup.exe',
          downloadFileName: 'LuckyNoteSetup.exe'
        },
        {
          title: '谷哥地球-天地图',
          shortName: '谷哥地球',
          tagline: '三维地球 · 天地图实景',
          icon: require('@/assets/earthview/logo_64x64.png'),
          preview: require('@/assets/earthview/地球-天安门.png'),
          description: 'Windows 三维地球客户端，基于天地图浏览全球与国内地标，支持中英文界面与流畅缩放。',
          features: ['三维地球实景浏览', '国内地标清晰可辨', '中英文界面切换'],
          route: '/product/earthview',
          downloadLink: '/ftp/packages/earthview/EarthViewSetup.exe',
          downloadFileName: 'EarthViewSetup.exe'
        },
        {
          title: '芊荨滚动长截图',
          shortName: '滚动长截图',
          tagline: '滚动截屏 · 智能拼接 · OCR',
          icon: require('@/assets/longshot/logo_64x64.png'),
          preview: require('@/assets/longshot/首页.png'),
          description: '自动滚动捕获超长页面，智能无缝拼接，内置预览编辑与 OCR 文字识别。',
          features: ['自动滚动截取长页面', '智能无缝拼接', 'OCR 识别与历史记录'],
          route: '/product/longshot',
          downloadLink: '/ftp/packages/longshot/LongShotSetup.exe',
          downloadFileName: 'LongShotSetup.exe'
        },
        {
          title: '周易星运八字测算大师',
          shortName: '国学文化',
          tagline: '黄历 · 八字 · 星座',
          icon: require('@/assets/fatestar/fatestar_64x64.png'),
          preview: require('@/assets/fatestar/首页.png'),
          description: '融汇传统历法与国学文化，提供黄历查询、八字排盘、星座测算、宝宝取名等工具。',
          features: ['黄历宜忌每日查询', '八字排盘与大运流年', '星座运势与缘分配对'],
          route: '/product/fatestar',
          downloadLink: '/ftp/packages/fatestar/FateStarSetup.exe',
          downloadFileName: 'FateStarSetup.exe'
        }
      ]
    }
  },
  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible')
            this.observer.unobserve(e.target)
          }
        })
      },
      { threshold: 0.08 }
    )
    this.$el.querySelectorAll('.reveal').forEach(el => this.observer.observe(el))
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
  }
}
</script>

<style scoped>
.home { padding-top: var(--header-h); }

/* ── Hero ── */
.hero {
  position: relative;
  background: var(--surface-dark);
  color: #fff;
  padding: 80px 0 0;
  overflow: hidden;
}

.hero-bg-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  opacity: 0.9;
  z-index: 0;
}

.hero-layout {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 48px;
  align-items: center;
  padding-bottom: 64px;
}

.hero-text h1 {
  font-size: clamp(2.25rem, 4.5vw, 3.25rem);
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin: 16px 0 20px;
}

.hero-text .overline { color: rgba(255, 255, 255, 0.45); }

.hero-desc {
  font-size: 16px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.6);
  max-width: 440px;
  margin-bottom: 32px;
}

.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-gallery {
  position: relative;
  height: 360px;
  min-height: 300px;
}

.hero-gallery .gallery-shot {
  position: absolute;
  width: 78%;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  transition: transform 0.35s var(--motion-ease);
}

/* 扇形散列叠放 — 桌面时钟居中置顶（同首次三图布局逻辑） */
.hero-gallery .gallery-shot--0 {
  left: 0;
  top: 56px;
  z-index: 1;
  transform: rotate(-2deg);
}

.hero-gallery .gallery-shot--1 {
  left: 0;
  top: 40px;
  z-index: 2;
  transform: rotate(-1.5deg);
}

.hero-gallery .gallery-shot--2 {
  right: 0;
  top: 48px;
  z-index: 3;
  transform: rotate(1.8deg);
}

.hero-gallery .gallery-shot--3 {
  right: 0;
  top: 30px;
  z-index: 4;
  transform: rotate(1.3deg);
}

.hero-gallery .gallery-shot--4 {
  left: 6%;
  top: 14px;
  z-index: 5;
  transform: rotate(-0.5deg);
}

.hero-gallery .gallery-shot--5 {
  left: 11%;
  top: 0;
  z-index: 6;
}

/* 最顶层：芊荨视频转 GIF 首页，叠在扇形组图最前方 */
.hero-gallery .gallery-shot--overlay {
  left: 11%;
  top: 0;
  z-index: 7;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.42);
}

.hero-gallery:hover .gallery-shot--0 { transform: rotate(-2deg) translateY(-4px); }
.hero-gallery:hover .gallery-shot--1 { transform: rotate(-1.5deg) translateY(-4px); }
.hero-gallery:hover .gallery-shot--2 { transform: rotate(1.8deg) translateY(-4px); }
.hero-gallery:hover .gallery-shot--3 { transform: rotate(1.3deg) translateY(-4px); }
.hero-gallery:hover .gallery-shot--4 { transform: rotate(-0.5deg) translateY(-5px); }
.hero-gallery:hover .gallery-shot--5 { transform: translateY(-6px); }
.hero-gallery:hover .gallery-shot--overlay { transform: translateY(-8px); }

/* ── Showcase ── */
.showcase {
  position: relative;
  padding: 100px 0 40px;
  scroll-margin-top: var(--header-h);
  overflow: hidden;
}

.showcase-bg-art {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 120%;
  max-width: 1400px;
  opacity: 0.55;
  pointer-events: none;
  z-index: 0;
}

.showcase-orbs-art {
  position: absolute;
  left: -60px;
  top: 10%;
  width: 280px;
  opacity: 0.7;
  pointer-events: none;
  z-index: 0;
}

.showcase .container,
.showcase-item {
  position: relative;
  z-index: 2;
}

.showcase-header {
  position: relative;
  max-width: 520px;
  margin-bottom: 64px;
}

.showcase-header h2 {
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 8px 0 12px;
}

.showcase-header p {
  color: var(--ink-muted);
  font-size: 16px;
  line-height: 1.7;
}

.showcase-item {
  position: relative;
  padding: 80px 0;
  border-top: 1px solid var(--line);
}

.showcase-item--alt {
  background: linear-gradient(180deg, var(--surface-subtle) 0%, rgba(250, 250, 250, 0.6) 100%);
}

.showcase-row {
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 64px;
  align-items: center;
}

.showcase-item--alt .showcase-row {
  direction: rtl;
}

.showcase-item--alt .showcase-row > * {
  direction: ltr;
}

.showcase-index {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-faint);
  letter-spacing: 0.05em;
  margin-bottom: 20px;
}

.showcase-title-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.showcase-icon {
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex-shrink: 0;
}

.showcase-copy h3 {
  font-size: 1.375rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.3;
}

.showcase-tag {
  font-size: 13px;
  color: var(--ink-muted);
  margin-top: 4px;
}

.showcase-desc {
  font-size: 15px;
  color: var(--ink-secondary);
  line-height: 1.75;
  margin-bottom: 20px;
}

.showcase-features {
  list-style: none;
  margin-bottom: 28px;
}

.showcase-features li {
  position: relative;
  padding-left: 16px;
  font-size: 14px;
  color: var(--ink-muted);
  margin-bottom: 8px;
}

.showcase-features li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--accent-brand);
}

.showcase-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.link-arrow {
  font-size: 14px;
  font-weight: 500;
  color: var(--ink-secondary);
  transition: color var(--motion-fast);
}

.link-arrow:hover { color: var(--accent-brand); }

.link-arrow::after {
  content: ' →';
}

/* ── Standards ── */
.standards {
  position: relative;
  padding: 100px 0;
  background: var(--surface-dark);
  color: #fff;
  overflow: hidden;
}

.standards-bg-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  opacity: 0.85;
  z-index: 0;
}

.standards .container {
  position: relative;
  z-index: 2;
}

.standards-header {
  margin-bottom: 48px;
}

.standards-header .overline { color: rgba(255, 255, 255, 0.4); }

.standards-header h2 {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  margin-top: 8px;
}

.standards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.standard {
  padding: 32px 28px;
  background: var(--surface-dark-elevated);
  transition: background var(--motion-fast);
}

.standard:hover {
  background: rgba(24, 24, 27, 0.95);
}

.standard-num {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--accent-brand);
  letter-spacing: 0.06em;
  margin-bottom: 16px;
}

.standard h4 {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 8px;
}

.standard p {
  font-size: 13px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.5);
}

/* ── About ── */
.about {
  position: relative;
  padding: 100px 0;
  border-top: 1px solid var(--line);
  overflow: hidden;
}

.about-bg-art {
  position: absolute;
  right: -80px;
  top: 50%;
  transform: translateY(-50%);
  width: 480px;
  opacity: 0.45;
  pointer-events: none;
  z-index: 0;
}

.about-layout {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 64px;
  align-items: start;
}

.about-copy h2 {
  font-size: clamp(1.5rem, 3vw, 1.875rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 8px 0 16px;
}

.about-copy p {
  color: var(--ink-secondary);
  line-height: 1.75;
  font-size: 15px;
}

.about-contact {
  margin-top: 20px;
  font-size: 14px;
}

.about-contact a {
  color: var(--accent-brand);
  font-weight: 500;
}

.about-contact a:hover { text-decoration: underline; }

.about-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.about-stat {
  padding: 28px 24px;
  background: var(--surface);
  text-align: center;
  transition: transform var(--motion-fast) var(--motion-ease), box-shadow var(--motion-fast);
}

.about-stat:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(4, 120, 87, 0.08);
}

.about-stat strong {
  display: block;
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.03em;
  margin-bottom: 4px;
}

.about-stat span {
  font-size: 13px;
  color: var(--ink-muted);
}

/* ── CTA ── */
.cta {
  position: relative;
  padding: 80px 0 100px;
  background: var(--surface-subtle);
  border-top: 1px solid var(--line);
  overflow: hidden;
}

.cta-bg-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  opacity: 0.9;
  z-index: 0;
}

.cta-inner {
  position: relative;
  z-index: 2;
  text-align: center;
}

.cta-inner h2 {
  font-size: clamp(1.375rem, 2.5vw, 1.75rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  margin-bottom: 28px;
}

/* ── Responsive ── */
@media (max-width: 1024px) {
  .hero-layout,
  .showcase-row,
  .about-layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .hero-gallery {
    height: auto;
    min-height: 0;
    max-width: 520px;
    margin: 0 auto;
    padding: 8px 0;
  }

  .hero-gallery .gallery-shot:not(.gallery-shot--overlay):not(:last-of-type) {
    display: none;
  }

  .hero-gallery .gallery-shot--overlay,
  .hero-gallery .gallery-shot:last-of-type {
    position: relative;
    width: 100%;
    left: auto !important;
    right: auto !important;
    top: auto !important;
    transform: none !important;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
  }

  .showcase-orbs-art { display: none; }

  .showcase-item--alt .showcase-row { direction: ltr; }

  .standards-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 640px) {
  .hero { padding-top: 48px; }
  .standards-grid { grid-template-columns: 1fr; }
  .about-stats { grid-template-columns: 1fr; }
}
</style>
